import React, { useState, useEffect, useMemo, useRef } from "react"
import {
  Button,
  GridTable,
  SecondaryHeader,
  SecondaryHeaderInfo,
  SecondaryHeaderSearchField,
} from "olive-react-myoneui"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import PaginationTemplate from "../component/grid-table/PaginationTemplate"
import TextTemplate from "../component/grid-table/TextTemplate"
import StatusLabel from "../component/StatusLabel"
import DropdownCommon from "../component/DropdownCommon"
import ConfirmBox from "../component/ConfirmBox"
import {
  penEditIcon,
  removeIcon,
  threeDotsIcon,
} from "../../../constants/icons"
import { getReports, deleteReport } from "../../../api/report.api"
import { debounce } from "../../../utils/helper"
import BasicContentLoader from "../../../components/loader/BasicContentLoader"

export default function Reports() {
  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [showConfirmBox, setShowConfirmBox] = useState(false)
  const [selectedReportId, setSelectedReportId] = useState(null)
  const [search, setSearch] = useState("")
  const [searchText, setSearchText] = useState("")

  const apiCallInProgress = useRef(false)

  const debouncedSetSearchText = useMemo(
    () =>
      debounce((val) => {
        setSearchText(val)
        setPage(1)
      }, 500),
    []
  )

  const onSearchChange = (e) => {
    const val = e.target.value
    setSearch(val)
    debouncedSetSearchText(val)
  }

  const onClearSearch = () => {
    setSearch("")
    setSearchText("")
    setPage(1)
  }

  function formatDate(dateString) {
    if (!dateString) return ""
    const date = new Date(dateString)
    const options = {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }
    return date
      .toLocaleString("en-GB", options)
      .replace("AM", "am")
      .replace("PM", "pm")
  }

  const fetchReports = async () => {
    if (apiCallInProgress.current) return
    apiCallInProgress.current = true
    setLoading(true)
    setError(null)

    try {
      const params = {
        page,
        limit: pageSize,
        ...(searchText ? { search: searchText } : {}), // Assuming API supports 'search' parameter
      }

      const res = await getReports(params)
      if (res?.data) {
        const formatted = res.data.data.map((report) => ({
          ...report,
          reporterName: `${report.reporter.firstName} ${report.reporter.lastName}`,
          createdAt: formatDate(report.createdAt),
        }))
        setReports(formatted)
        setTotalItems(res.data.pagination.totalItems || 0)
        setPageSize(res.data.pagination.pageSize || pageSize)
      }
    } catch (err) {
      console.error("Error fetching reports:", err)
      setError(err)
      toast.error(
        err?.response?.data?.message ||
          "Failed to fetch reports. Please try again."
      )
    } finally {
      setLoading(false)
      apiCallInProgress.current = false
    }
  }

  useEffect(() => {
    fetchReports()
  }, [page, pageSize, searchText])

  const handlePageChange = (newPage) => {
    if (!loading) setPage(newPage)
  }

  const handlePageSizeChange = (newSize) => {
    if (!loading && newSize !== pageSize) {
      setPageSize(newSize)
      setPage(1)
    }
  }

  const handleDeleteReport = (id) => {
    setSelectedReportId(id)
    setShowConfirmBox(true)
  }

  const deleteReportById = async () => {
    setLoading(true)
    try {
      await deleteReport(selectedReportId)
      toast.success(`Report ${selectedReportId} deleted successfully.`)
      setShowConfirmBox(false)
      setSelectedReportId(null)
      fetchReports() // Refresh the list
    } catch (err) {
      console.error("Error deleting report:", err)
      toast.error(
        err?.response?.data?.message ||
          "Failed to delete report. Please try again."
      )
    } finally {
      setLoading(false)
    }
  }

  const handleChangeStatus = async (id, newStatus) => {
    // Placeholder for actual API call to change report status
    // Example: await updateReportStatus(id, newStatus);
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500))
      toast.success(`Report ${id} status updated to ${newStatus}.`)
      fetchReports() // Refresh the list
    } catch (err) {
      console.error("Error changing report status:", err)
      toast.error("Failed to change report status. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleOpenChangeStatusModal = (id) => {
    // Placeholder for opening a modal to change status
    toast.info(`Opening change status modal for report ${id}`)
  }

  const columnData = [
    {
      id: 1,
      field: "reporterName",
      label: "Reporter",
      width: "1fr",
      cellRenderer: TextTemplate,
    },
    {
      id: 2,
      field: "paper",
      label: "Papper",
      width: "1fr",
      cellRenderer: ({ data }) => <p>{data.questionId.paper}</p>,
    },
    {
      id: 2,
      field: "detail",
      label: "Detail",
      width: "3fr",
      cellRenderer: ({ data }) => (
        <p
          className='p-2'
          style={{
            textAlign: "justify",
            marginRight: "30px",
          }}
        >
          {data.detail}
        </p>
      ),
    },
    {
      id: 3,
      field: "screenshot",
      label: "Screenshot",
      width: "1fr",
      cellRenderer: ({ data }) =>
        data.screenshot ? (
          <a href={data.screenshot} target='_blank' rel='noopener noreferrer'>
            <img
              src={data.screenshot}
              alt='Screenshot'
              style={{ width: "50px", height: "50px", objectFit: "cover" }}
            />
          </a>
        ) : (
          <span>N/A</span>
        ),
    },
    {
      id: 4,
      field: "status",
      label: "Status",
      width: "0.5fr",
      cellRenderer: ({ data }) => {
        let backgroundColor = ""
        switch (data.status) {
          case "pending":
            backgroundColor = "#ffc107" // Bootstrap yellow/orange
            break
          case "resolved":
            backgroundColor = "#28a745" // Bootstrap green
            break
          case "rejected":
            backgroundColor = "#dc3545" // Bootstrap red
            break
          default:
            backgroundColor = "#6c757d" // Bootstrap gray
        }
        return (
          <span
            style={{
              backgroundColor: backgroundColor,
              color: "white",
              padding: "0.25em 0.5em",
              borderRadius: "0.25rem",
              fontWeight: "bold",
              textTransform: "capitalize",
            }}
          >
            {data.status}
          </span>
        )
      },
    },
    {
      id: 5,
      label: "Action",
      width: "0.5fr",
      className: "grid-header-text-center",
      cellRenderer: ({ data }) => (
        <div className='rgt-cell-inner program-edit-hover justify-content-center'>
          {/* <Button
            variant='white'
            className='btn-round'
            onClick={() => handleOpenChangeStatusModal(data._id)}
            disabled={loading}
          >
            {penEditIcon} Change Status
          </Button> */}
          <Button
            variant='white'
            className='btn-round'
            onClick={() => handleDeleteReport(data._id)}
            disabled={loading}
          >
            {removeIcon} Delete
          </Button>
        </div>
      ),
    },
  ]

  return (
    <>
      <div className='content-wrapper position-relative'>
        <SecondaryHeader>
          <SecondaryHeaderInfo
            SecondaryHeaderTitle='Reports'
            SecondaryHeaderText={`${totalItems} Reports`}
            SecondaryText
          />
          <SecondaryHeaderSearchField
            placeholder='Search Reports...'
            value={search}
            onChange={onSearchChange}
            onClear={onClearSearch}
            disabled={loading}
          />
          <div className='secondary-header-buttons'>
            {/* Add any additional buttons here if needed */}
          </div>
        </SecondaryHeader>

        {/* Loader Overlay */}
        {loading && (
          <div className='overlay-loader'>
            <BasicContentLoader />
          </div>
        )}

        {!loading && (
          <div className='content-block p-2'>
            {reports.length > 0 ? (
              <GridTable
                key={reports.length}
                searchText={searchText}
                highlightSearch={false}
                columns={columnData}
                onRowsRequest={() => ({
                  rows: reports,
                  totalRows: totalItems,
                })}
                page={page}
                pageSize={pageSize}
                setPage={handlePageChange}
                setPageSize={handlePageSizeChange}
                totalPages={Math.ceil(totalItems / pageSize)}
                filter={null} // No specific filter for now
                components={{
                  Footer: (props) => (
                    <PaginationTemplate
                      {...props}
                      page={page}
                      pageSize={pageSize}
                      totalPages={Math.ceil(totalItems / pageSize)}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                      disabled={loading}
                    />
                  ),
                }}
              />
            ) : (
              <div className='text-center py-5 text-muted'>
                No reports found.
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmBox
        show={showConfirmBox}
        question='Are you sure you want to delete this report?'
        okText='Delete'
        cancelText='Cancel'
        onHide={() => setShowConfirmBox(false)}
        onSubmit={deleteReportById}
      />

      <ToastContainer position='top-right' autoClose={5000} />
    </>
  )
}
